import { BACKEND_URL, forceLogout } from "./api";

/**
 * Opens a chat WebSocket with auto-reconnect (exponential backoff).
 * Returns a wrapper { close } to allow caller to permanently close.
 *
 * Options:
 *   onMessage(data)  – called for every JSON message (excluding pong)
 *   onReconnect()    – called when the socket re-opens after a drop
 *                      (NOT called on the very first connection)
 */
export function openChatSocket(token, onMessage, onReconnect) {
  const wsBase = BACKEND_URL.replace(/^http/, "ws");
  const url = `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;

  let ws = null;
  let pingInterval = null;
  let reconnectTimer = null;
  let attempts = 0;
  let everConnected = false;
  let closedByUser = false;

  const connect = () => {
    if (closedByUser) return;
    ws = new WebSocket(url);

    ws.addEventListener("open", () => {
      const wasReconnect = everConnected;
      everConnected = true;
      attempts = 0;
      if (pingInterval) clearInterval(pingInterval);
      pingInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, 25000);
      if (wasReconnect && typeof onReconnect === "function") {
        try {
          onReconnect();
        } catch (_) {}
      }
    });

    ws.addEventListener("message", (evt) => {
      if (evt.data === "pong") return;
      try {
        const data = JSON.parse(evt.data);
        if (data?.type === "force_logout") {
          closedByUser = true;
          forceLogout(data.reason);
          return;
        }
        onMessage(data);
      } catch (_) {
        // ignore
      }
    });

    const scheduleReconnect = (evt) => {
      if (closedByUser) return;
      if (evt?.code === 4401) {
        closedByUser = true;
        forceLogout();
        return;
      }
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      attempts += 1;
      const delay = Math.min(30000, 500 * 2 ** Math.min(attempts, 6));
      reconnectTimer = setTimeout(connect, delay);
    };

    ws.addEventListener("close", scheduleReconnect);
    ws.addEventListener("error", () => {
      try {
        ws.close();
      } catch (_) {}
    });
  };

  connect();

  return {
    close: () => {
      closedByUser = true;
      if (pingInterval) clearInterval(pingInterval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        ws && ws.close();
      } catch (_) {}
    },
  };
}
